import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';

interface AutoSaveNotificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  characterName: string;
  rulesetId: string;
}

/**
 * Dialog shown when a user creates a new character while one is already in progress.
 * Informs the user that their character has been automatically saved to the library.
 */
export function AutoSaveNotificationDialog({
  isOpen,
  onClose,
  onContinue,
  characterName,
  rulesetId,
}: AutoSaveNotificationDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      const timer = setTimeout(() => {
        window.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const displayName = characterName.trim() || 'Your character';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm animate-fade-in"
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          className={clsx(
            'w-full max-w-md bg-background-card rounded-xl border border-border-default',
            'shadow-2xl animate-scale-in'
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
          aria-describedby="dialog-description"
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckIcon className="w-5 h-5 text-green-400" />
              </div>
              <h2
                id="dialog-title"
                className="text-lg font-semibold text-text-primary"
              >
                Character Saved!
              </h2>
            </div>
            <p id="dialog-description" className="text-text-secondary">
              <span className="font-medium text-text-primary">{displayName}</span> has been
              automatically saved to your Character Library. You can continue editing
              or access it anytime from your library.
            </p>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex gap-3">
            <Link
              to={`/${rulesetId}/library`}
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-border-default text-text-secondary font-medium hover:bg-background-elevated transition-colors text-center"
            >
              View Library
            </Link>
            <button
              type="button"
              onClick={onContinue}
              className="flex-1 px-4 py-2.5 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
